/**
 * BulkBarcodeImport - Bulk barcode import: paste/upload -> PREVIEW -> pick -> confirm -> undo.
 *
 * PLATFORM:F1.P9-10 Phase 3 / V-BARCODE.
 *
 * ⛔⛔ THIS USED TO BE ONE CLICK THAT WROTE STRAIGHT INTO THE VAULT. The old route looped every
 * barcode through `scan_barcode_to_vault(auto_confirm=True)`: any >=90% match was committed per
 * item, with no import log (so no rollback), and the user was shown a "matched/unmatched" tally
 * AFTER the fact. That is the auto-applied identity the operator's tenet bans — "having a user make
 * a selection, even at 1 result, mitigates risk associated with misidentification then commitment
 * to end user vaults."
 *
 * Now `POST /vault/import/barcode/bulk` is a PREVIEW (`{import_id, total, matched, unmatched,
 * candidates: [{barcode, matched, confidence, candidates: [...]}], expires_in_s}`) and nothing
 * enters the vault until the user TICKS the rows to import — picking a candidate where there is
 * more than one — and confirms. A single candidate is highlighted, never pre-ticked: a set of one
 * is still a set. The confirm returns the CLZImportResult shape with an `import_id`, and Undo
 * calls the existing rollback route against it.
 *
 * Two hosts: a standalone modal (`isOpen`/`onClose`) and a tab body inside AddToVault
 * (`embedded`), so the flow is written once.
 */
import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Check, AlertTriangle, Loader2, FileText, Layers, RotateCcw } from 'lucide-react';
import { bulkBarcodeImport, confirmBulkBarcodeImport, rollbackImport } from '../../services/vaultApi';

/**
 * A candidate is a comic (`comic_id`) or a product (`product_id`). The confirm body names it as
 * `{match_type, match_id}`; the server normalises 'comic' -> 'comic_issue'.
 */
export function candidateSelection(barcode, candidate) {
  const isComic = candidate.comic_id !== null && candidate.comic_id !== undefined;
  return {
    barcode,
    match_type: isComic ? 'comic' : 'product',
    match_id: isComic ? candidate.comic_id : candidate.product_id,
  };
}

function candidateKey(candidate) {
  const sel = candidateSelection('', candidate);
  return `${sel.match_type}:${sel.match_id}`;
}

function CandidateOption({ candidate, picked, highlighted, onPick, testId }) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={picked}
      className={`w-full text-left flex gap-2 items-center rounded-lg px-2 py-1.5 border transition-colors ${
        picked
          ? 'border-[#D4A03C] bg-[#D4A03C]/10'
          : highlighted
            ? 'border-[#D4A03C]/40 bg-zinc-800'
            : 'border-zinc-700 bg-zinc-800 hover:border-zinc-500'
      }`}
      data-testid={testId}
    >
      <div className="w-8 h-11 bg-zinc-700 rounded overflow-hidden flex-shrink-0 flex items-center justify-center">
        {candidate.cover_url ? (
          <img src={candidate.cover_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <Layers size={12} className="text-zinc-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-xs font-medium truncate">{candidate.title || 'Unknown Title'}</p>
        <p className="text-zinc-400 text-[11px] truncate">
          {candidate.issue_number ? `#${candidate.issue_number}` : ''}
          {candidate.issue_number && candidate.publisher ? ' · ' : ''}
          {candidate.publisher || ''}
          {candidate.product_type ? ` · ${candidate.product_type}` : ''}
        </p>
      </div>
      {picked && <Check size={14} className="text-[#D4A03C] flex-shrink-0" />}
    </button>
  );
}

/**
 * One barcode of the preview: its tick box, its confidence, and its candidate(s).
 * Rendered from the SERVED preview row shape, never from a hand-typed one.
 */
function ResultRow({ row, idx, ticked, pickedKey, onTick, onPick }) {
  const candidates = row.candidates;
  const single = candidates.length === 1;
  const canTick = candidates.length > 0 && (single || pickedKey !== undefined);
  const hasConfidence = typeof row.confidence === 'number';

  return (
    <div
      className={`px-2 py-2 ${ticked ? 'bg-[#D4A03C]/5' : ''}`}
      data-testid={`bulk-barcode-row-${idx}`}
      data-ticked={String(ticked)}
    >
      <div className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={ticked}
          disabled={!canTick}
          onChange={() => onTick(row)}
          aria-label={`Import ${row.barcode}`}
          className="w-4 h-4 rounded border-zinc-600 disabled:opacity-40"
          data-testid={`bulk-barcode-tick-${idx}`}
        />
        {candidates.length > 0 ? (
          <Check size={14} className="text-green-400 flex-shrink-0" />
        ) : (
          <AlertTriangle size={14} className="text-yellow-400 flex-shrink-0" />
        )}
        <span className="font-mono text-zinc-300 text-xs flex-shrink-0 w-32 truncate">{row.barcode}</span>
        <span className={`text-xs truncate ${candidates.length > 0 ? 'text-zinc-400' : 'text-yellow-400'}`}>
          {candidates.length === 0
            ? 'No match'
            : single
              ? '1 candidate — tick to confirm'
              : `${candidates.length} candidates — pick one`}
        </span>
        {hasConfidence && (
          <span className="ml-auto text-[11px] text-zinc-500" data-testid={`bulk-barcode-confidence-${idx}`}>
            {row.confidence}%
          </span>
        )}
      </div>
      {candidates.length > 0 && (
        <div className="mt-1.5 ml-6 space-y-1">
          {candidates.map((candidate, j) => {
            const key = candidateKey(candidate);
            return (
              <CandidateOption
                key={key}
                candidate={candidate}
                picked={pickedKey === key}
                highlighted={single}
                onPick={() => onPick(row, candidate)}
                testId={`bulk-barcode-candidate-${idx}-${j}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

const INITIAL = {
  step: 'input', // input | preview | result | undone | expired
  file: null,
  text: '',
  preview: null,
  result: null,
  error: null,
  removed: null,
};

export default function BulkBarcodeImport({ isOpen = true, onClose, onComplete, embedded = false }) {
  const [state, setState] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  // barcode -> candidateKey of the picked candidate; barcode -> true when ticked
  const [picks, setPicks] = useState({});
  const [ticks, setTicks] = useState({});
  const fileInputRef = useRef(null);

  const patch = (next) => setState((prev) => ({ ...prev, ...next }));

  const resetAll = () => {
    setState(INITIAL);
    setPicks({});
    setTicks({});
    setDragOver(false);
  };

  // A closed modal drops its state so a re-open starts at the input step.
  useEffect(() => {
    if (!embedded && !isOpen) {
      setState(INITIAL);
      setPicks({});
      setTicks({});
    }
  }, [isOpen, embedded]);

  if (!embedded && !isOpen) return null;

  const { step, file, text, preview, result, error, removed } = state;

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['txt', 'csv'].includes(ext)) {
      patch({ error: 'Please upload a .txt or .csv file' });
      return;
    }
    patch({ file: selectedFile, error: null });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer?.files?.[0];
    if (droppedFile) handleFileSelect(droppedFile);
  };

  const pastedBarcodes = text
    .split(/[\s,;]+/)
    .map((b) => b.trim())
    .filter(Boolean);
  const canPreview = Boolean(file) || pastedBarcodes.length > 0;

  // The buttons' `disabled` state is the double-submit guard: a disabled button never fires onClick.
  const handlePreview = async () => {
    setBusy(true);
    patch({ error: null });
    try {
      // A pasted list becomes the same one-barcode-per-line file the route already accepts, so the
      // request shape is identical whichever way the user handed the codes over.
      const payload = file || new File([pastedBarcodes.join('\n')], 'barcodes.txt', { type: 'text/plain' });
      const data = await bulkBarcodeImport(payload);
      setPicks({});
      setTicks({});
      patch({ preview: data, step: 'preview' });
    } catch (err) {
      patch({ error: err.message || 'Bulk import failed' });
    } finally {
      setBusy(false);
    }
  };

  const rows = preview ? preview.candidates : [];

  const handlePick = (row, candidate) => {
    const key = candidateKey(candidate);
    setPicks((prev) => ({ ...prev, [row.barcode]: key }));
    // Picking a candidate IS the confirmation for that barcode — it ticks the row.
    setTicks((prev) => ({ ...prev, [row.barcode]: true }));
  };

  const handleTick = (row) => {
    const currently = Boolean(ticks[row.barcode]);
    if (currently) {
      setTicks((prev) => ({ ...prev, [row.barcode]: false }));
      return;
    }
    // A single candidate is highlighted, never pre-picked; ticking the row is the user's pick.
    if (row.candidates.length === 1 && picks[row.barcode] === undefined) {
      setPicks((prev) => ({ ...prev, [row.barcode]: candidateKey(row.candidates[0]) }));
    }
    setTicks((prev) => ({ ...prev, [row.barcode]: true }));
  };

  const selections = rows
    .filter((row) => ticks[row.barcode] && picks[row.barcode] !== undefined)
    .map((row) => {
      const candidate = row.candidates.find((c) => candidateKey(c) === picks[row.barcode]);
      return candidateSelection(row.barcode, candidate);
    });

  const handleConfirm = async () => {
    setBusy(true);
    patch({ error: null });
    try {
      const data = await confirmBulkBarcodeImport(preview.import_id, selections);
      patch({ result: data, step: 'result' });
      onComplete?.(data);
    } catch (err) {
      if (err.status === 404) {
        // The preview cache is TTL-bound (`expires_in_s`); once it is gone there is nothing to
        // confirm against and the only honest move is to start over.
        patch({ step: 'expired' });
      } else {
        patch({ error: err.message || 'Bulk import failed' });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleUndo = async () => {
    setBusy(true);
    patch({ error: null });
    try {
      const data = await rollbackImport(result.import_id);
      patch({ step: 'undone', removed: data.items_removed });
      onComplete?.(data);
    } catch (err) {
      patch({ error: err.message || 'Undo failed' });
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    resetAll();
    onClose?.();
  };

  const expiresMinutes = typeof preview?.expires_in_s === 'number'
    ? Math.max(1, Math.round(preview.expires_in_s / 60))
    : null;

  const body = (
    <div className="px-4 py-4 space-y-4">
      {/* Step 1: paste or upload */}
      {step === 'input' && (
        <>
          <textarea
            value={text}
            onChange={(e) => patch({ text: e.target.value, error: null })}
            placeholder={'Paste barcodes, one per line\n75960608936800111\n76194134757400121'}
            rows={4}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-white font-mono text-xs placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#D4A03C] resize-none"
            data-testid="bulk-barcode-text"
          />
          <div className="flex items-center gap-2 text-zinc-500 text-xs">
            <span className="flex-1 h-px bg-zinc-800" />
            or upload a file
            <span className="flex-1 h-px bg-zinc-800" />
          </div>
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
              dragOver
                ? 'border-[#D4A03C] bg-[#D4A03C]/5'
                : 'border-zinc-700 hover:border-zinc-600'
            }`}
            onClick={() => fileInputRef.current.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            data-testid="bulk-barcode-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.csv"
              className="hidden"
              onChange={(e) => handleFileSelect(e.target.files?.[0])}
              data-testid="bulk-barcode-file-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-2">
                <FileText size={20} className="text-[#D4A03C]" />
                <span className="text-white text-sm">{file.name}</span>
                <span className="text-zinc-500 text-xs">
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
              </div>
            ) : (
              <>
                <Upload size={32} className="mx-auto text-zinc-500 mb-2" />
                <p className="text-zinc-400 text-sm">
                  Drop a text file here or click to browse
                </p>
                <p className="text-zinc-500 text-xs mt-1">
                  One barcode per line (.txt or .csv)
                </p>
              </>
            )}
          </div>

          <button
            onClick={handlePreview}
            disabled={!canPreview || busy}
            className="w-full bg-[#D4A03C] hover:bg-[#E0B04C] text-black font-semibold rounded-xl py-2.5 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ minHeight: '44px' }}
            data-testid="bulk-barcode-import-btn"
          >
            {busy ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Matching...
              </>
            ) : (
              <>
                <Upload size={16} />
                Preview Matches
              </>
            )}
          </button>
        </>
      )}

      {/* Step 2: preview — tick rows, pick candidates */}
      {step === 'preview' && preview && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-zinc-800 rounded-xl p-3 text-center">
              <p className="text-zinc-500 text-xs">Total</p>
              <p className="text-white text-lg font-bold">{preview.total}</p>
            </div>
            <div className="bg-zinc-800 rounded-xl p-3 text-center">
              <p className="text-zinc-500 text-xs">Matched</p>
              <p className="text-green-400 text-lg font-bold">{preview.matched}</p>
            </div>
            <div className="bg-zinc-800 rounded-xl p-3 text-center">
              <p className="text-zinc-500 text-xs">Unmatched</p>
              <p className="text-yellow-400 text-lg font-bold">{preview.unmatched}</p>
            </div>
          </div>

          <p className="text-zinc-400 text-xs">
            Nothing is in your vault yet. Tick the barcodes to import; where there is more than one
            candidate, pick the right book.
            {expiresMinutes !== null && (
              <span className="text-zinc-500" data-testid="bulk-barcode-expiry">
                {' '}This preview expires in {expiresMinutes} min.
              </span>
            )}
          </p>

          <div className="bg-zinc-800/50 rounded-xl max-h-72 overflow-y-auto divide-y divide-zinc-800">
            {rows.map((row, idx) => (
              <ResultRow
                key={`${row.barcode}-${idx}`}
                row={row}
                idx={idx}
                ticked={Boolean(ticks[row.barcode])}
                pickedKey={picks[row.barcode]}
                onTick={handleTick}
                onPick={handlePick}
              />
            ))}
          </div>

          <div className="flex gap-2">
            <button
              onClick={resetAll}
              disabled={busy}
              className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl py-2.5 text-sm transition-colors disabled:opacity-50"
              style={{ minHeight: '44px' }}
              data-testid="bulk-barcode-back"
            >
              Start Over
            </button>
            <button
              onClick={handleConfirm}
              disabled={selections.length === 0 || busy}
              className="flex-1 bg-[#D4A03C] hover:bg-[#E0B04C] text-black font-semibold rounded-xl py-2.5 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ minHeight: '44px' }}
              data-testid="bulk-barcode-confirm-btn"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Add {selections.length} to Vault
            </button>
          </div>
        </>
      )}

      {/* Step 3: result with Undo */}
      {step === 'result' && result && (
        <>
          <div className="grid grid-cols-2 gap-3" data-testid="bulk-barcode-result">
            <div className="bg-zinc-800 rounded-xl p-3 text-center">
              <p className="text-zinc-500 text-xs">Imported</p>
              <p className="text-green-400 text-lg font-bold">{result.rows_imported}</p>
            </div>
            <div className="bg-zinc-800 rounded-xl p-3 text-center">
              <p className="text-zinc-500 text-xs">Matched</p>
              <p className="text-blue-400 text-lg font-bold">{result.rows_matched}</p>
            </div>
            <div className="bg-zinc-800 rounded-xl p-3 text-center">
              <p className="text-zinc-500 text-xs">Skipped</p>
              <p className="text-yellow-400 text-lg font-bold">{result.rows_skipped}</p>
            </div>
            <div className="bg-zinc-800 rounded-xl p-3 text-center">
              <p className="text-zinc-500 text-xs">Failed</p>
              <p className="text-red-400 text-lg font-bold">{result.rows_failed}</p>
            </div>
          </div>
          {result.points_awarded > 0 && (
            <p className="text-center text-[#D4A03C] text-sm font-medium">
              +{result.points_awarded} points earned
            </p>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleUndo}
              disabled={busy}
              className="flex-1 flex items-center justify-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl py-2.5 text-sm transition-colors disabled:opacity-50"
              style={{ minHeight: '44px' }}
              data-testid="bulk-barcode-undo"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
              Undo
            </button>
            <button
              onClick={handleClose}
              className="flex-1 bg-[#D4A03C] hover:bg-[#E0B04C] text-black font-semibold rounded-xl py-2.5 text-sm transition-colors"
              style={{ minHeight: '44px' }}
              data-testid="bulk-barcode-done"
            >
              Done
            </button>
          </div>
        </>
      )}

      {/* Undone */}
      {step === 'undone' && (
        <div className="text-center space-y-3" data-testid="bulk-barcode-undone">
          <p className="text-white text-sm font-medium">
            Import undone — {removed} item{removed === 1 ? '' : 's'} removed.
          </p>
          <button
            onClick={handleClose}
            className="w-full bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl py-2.5 text-sm transition-colors"
            style={{ minHeight: '44px' }}
            data-testid="bulk-barcode-done"
          >
            Done
          </button>
        </div>
      )}

      {/* Expired preview */}
      {step === 'expired' && (
        <div className="text-center space-y-3" data-testid="bulk-barcode-expired">
          <AlertTriangle size={28} className="mx-auto text-yellow-400" />
          <p className="text-white text-sm font-medium">This preview expired.</p>
          <p className="text-zinc-400 text-xs">Nothing was added. Paste or upload the barcodes again.</p>
          <button
            onClick={resetAll}
            className="w-full bg-[#D4A03C] hover:bg-[#E0B04C] text-black font-semibold rounded-xl py-2.5 text-sm transition-colors"
            style={{ minHeight: '44px' }}
            data-testid="bulk-barcode-start-over"
          >
            Start Over
          </button>
        </div>
      )}

      {error && (
        <p className="text-red-400 text-sm" data-testid="bulk-barcode-error">
          {error}
        </p>
      )}
    </div>
  );

  if (embedded) {
    return <div data-testid="bulk-barcode-embedded">{body}</div>;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      data-testid="bulk-barcode-dialog"
    >
      <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
          <h2 className="text-white font-semibold">Bulk Barcode Import</h2>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-zinc-800 transition-colors"
            aria-label="Close"
            data-testid="bulk-barcode-close"
          >
            <X size={18} className="text-zinc-400" />
          </button>
        </div>
        {body}
      </div>
    </div>
  );
}
